import type { ExerciseTemplate } from '@yuan-math/exercises';
import { roundTo } from '@yuan-math/math';
import { choiceExercise, numericExercise, options, template, trap } from './authoring.ts';

/** Coordinate geometry, area beyond the rectangle, volume, and statistics. */
export const GEOMETRY_MIDDLE_TEMPLATES: ExerciseTemplate[] = [
  /* --------------------------------------------------- coordinate plane -- */
  template({
    id: 'coordinate-read',
    conceptId: 'coordinate-plane',
    description: 'Read the coordinates of a plotted point.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      // Difficulty 1 stays in the first quadrant; higher difficulties use all four.
      const x = difficulty === 1 ? rng.int(1, 5) : rng.int(-5, 5);
      const y = difficulty === 1 ? rng.int(1, 5) : rng.int(-5, 5);
      const askX = rng.bool();
      return numericExercise({
        prompt: `What is the ${askX ? 'x' : 'y'}-coordinate of point A?`,
        figure: { kind: 'coordinate-grid', min: -5, max: 5, points: [{ x, y, label: 'A' }] },
        value: askX ? x : y,
        summary: `A is at (${x}, ${y}), so its ${askX ? 'x' : 'y'}-coordinate is ${askX ? x : y}.`,
        hint: 'The first number is across; the second is up.',
        steps: [
          { label: 'Read across to the point, then up.', latex: `(${x}, ${y})` },
          { label: `Take the ${askX ? 'first' : 'second'} number.`, latex: String(askX ? x : y) },
        ],
        // Only a trap when the two coordinates actually differ.
        ...(x !== y ? { traps: [trap(askX ? y : x, 'coordinates-reversed')] } : {}),
      });
    },
  }),

  template({
    id: 'coordinate-quadrant',
    conceptId: 'coordinate-plane',
    description: 'Name the quadrant a point lies in.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const x = rng.pick([-1, 1]) * rng.int(1, 5);
      const y = rng.pick([-1, 1]) * rng.int(1, 5);
      const quadrant = x > 0 ? (y > 0 ? 'I' : 'IV') : y > 0 ? 'II' : 'III';
      const { choices, correctId } = options(
        (['I', 'II', 'III', 'IV'] as const).map((label) => ({ label, correct: label === quadrant })),
        rng.shuffle,
      );
      return choiceExercise({
        prompt: `Which quadrant contains the point (${x}, ${y})?`,
        figure: { kind: 'coordinate-grid', min: -5, max: 5, showQuadrantLabels: true, points: [{ x, y }] },
        choices,
        correctId,
        summary: `x is ${x > 0 ? 'positive' : 'negative'} and y is ${y > 0 ? 'positive' : 'negative'}, which is quadrant ${quadrant}.`,
        hint: 'Quadrants are numbered anticlockwise, starting from the top right.',
        steps: [
          { label: 'Check the sign of each coordinate.', latex: `x ${x > 0 ? '>' : '<'} 0, \\quad y ${y > 0 ? '>' : '<'} 0` },
          { label: 'That fixes the quadrant.', latex: `\\text{Quadrant } ${quadrant}` },
        ],
      });
    },
  }),

  template({
    id: 'coordinate-distance',
    conceptId: 'coordinate-plane',
    description: 'Find the distance between two points on the same line.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const horizontal = rng.bool();
      const shared = rng.int(-4, 4);
      let a = rng.int(-5, 5);
      let b = rng.int(-5, 5);
      if (a === b) b = a === 5 ? a - 1 : a + 1;
      const distance = Math.abs(a - b);
      const points = horizontal
        ? [{ x: a, y: shared, label: 'A' }, { x: b, y: shared, label: 'B' }]
        : [{ x: shared, y: a, label: 'A' }, { x: shared, y: b, label: 'B' }];
      return numericExercise({
        prompt: 'How far apart are points A and B?',
        figure: { kind: 'coordinate-grid', min: -5, max: 5, points },
        unit: 'units',
        value: distance,
        summary: `They share a ${horizontal ? 'y' : 'x'}-coordinate, so count along the line: ${distance} units.`,
        hint: 'The points sit on the same straight line, so just count the gap.',
        steps: [
          { label: 'Notice which coordinate they share.', latex: horizontal ? `y = ${shared}` : `x = ${shared}` },
          { label: 'Find the gap between the others.', latex: `|${a} - ${b}| = ${distance}` },
        ],
        traps: [trap(a + b, 'coordinates-reversed')],
      });
    },
  }),

  /* ---------------------------------------------------------------- area */
  template({
    id: 'triangle-area',
    conceptId: 'area-triangles',
    description: 'Find the area of a triangle.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const ceiling = difficulty === 2 ? 10 : difficulty === 3 ? 18 : 30;
      // An even base keeps the halving exact at the easier difficulties.
      const base = difficulty === 2 ? rng.int(1, ceiling / 2) * 2 : rng.int(2, ceiling);
      const height = rng.int(2, ceiling);
      const area = roundTo((base * height) / 2, 2);
      return numericExercise({
        prompt: `A triangle has a base of ${base} cm and a height of ${height} cm. What is its area?`,
        figure: { kind: 'shape', shape: 'triangle', labels: { base: `${base} cm`, height: `${height} cm` } },
        unit: 'cm²',
        value: area,
        summary: `½ × ${base} × ${height} = ${area} cm².`,
        hint: 'A triangle is half of the rectangle around it.',
        steps: [
          { label: 'Start with the surrounding rectangle.', latex: `${base} \\times ${height} = ${base * height}` },
          { label: 'Halve it.', latex: `\\tfrac{1}{2} \\times ${base * height} = ${area}` },
        ],
        traps: [trap(base * height, 'triangle-area-no-half'), trap(2 * (base + height), 'perimeter-for-area')],
      });
    },
  }),

  template({
    id: 'parallelogram-area',
    conceptId: 'area-triangles',
    description: 'Find the area of a parallelogram.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const base = rng.int(3, difficulty === 2 ? 12 : 25);
      const height = rng.int(2, difficulty === 2 ? 10 : 20);
      return numericExercise({
        prompt: `A parallelogram has a base of ${base} cm and a perpendicular height of ${height} cm. What is its area?`,
        unit: 'cm²',
        value: base * height,
        summary: `Cut and rearranged into a rectangle: ${base} × ${height} = ${base * height} cm².`,
        hint: 'A parallelogram rearranges into a rectangle with the same base and height.',
        steps: [
          { label: 'Multiply base by perpendicular height.', latex: `${base} \\times ${height}` },
          { label: 'Area, in square units.', latex: `${base * height}\\text{ cm}^2` },
        ],
        // Halving here is the mirror-image error of forgetting it on a triangle.
        traps: [trap(roundTo((base * height) / 2, 2), 'triangle-area-no-half')],
      });
    },
  }),

  template({
    id: 'triangle-missing-side',
    conceptId: 'area-triangles',
    description: 'Work back from an area to a missing base or height.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const base = rng.int(2, 12) * 2;
      const height = rng.int(3, 15);
      const area = (base * height) / 2;
      return numericExercise({
        prompt: `A triangle has an area of ${area} cm² and a base of ${base} cm. What is its height?`,
        unit: 'cm',
        value: height,
        summary: `Area = ½ × base × height, so height = 2 × ${area} ÷ ${base} = ${height} cm.`,
        hint: 'Undo the formula: double the area first, then divide by the base.',
        steps: [
          { label: 'Start from the formula.', latex: `${area} = \\tfrac{1}{2} \\times ${base} \\times h` },
          { label: 'Double both sides to remove the half.', latex: `${2 * area} = ${base} \\times h` },
          { label: 'Divide by the base.', latex: `h = ${height}` },
        ],
        traps: [trap(roundTo(area / base, 3), 'triangle-area-no-half')],
      });
    },
  }),

  /* -------------------------------------------------------------- volume */
  template({
    id: 'prism-volume',
    conceptId: 'volume',
    description: 'Find the volume of a rectangular prism.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const ceiling = difficulty === 1 ? 5 : difficulty === 2 ? 10 : 20;
      const length = rng.int(2, ceiling);
      const width = rng.int(2, ceiling);
      const height = rng.int(2, ceiling);
      return numericExercise({
        prompt: `A box is ${length} cm long, ${width} cm wide and ${height} cm tall. What is its volume?`,
        figure: { kind: 'prism', length, width, height, labels: { length: `${length} cm`, width: `${width} cm`, height: `${height} cm` } },
        unit: 'cm³',
        value: length * width * height,
        summary: `${length} × ${width} × ${height} = ${length * width * height} cm³.`,
        hint: 'Find the area of the base, then multiply by how many layers tall it is.',
        steps: [
          { label: 'Area of the base — one layer of cubes.', latex: `${length} \\times ${width} = ${length * width}` },
          { label: 'Multiply by the height.', latex: `${length * width} \\times ${height} = ${length * width * height}` },
        ],
        traps: [trap(length * width, 'volume-as-area')],
      });
    },
  }),

  template({
    id: 'prism-missing-edge',
    conceptId: 'volume',
    description: 'Find a missing edge from the volume.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const length = rng.int(2, 12);
      const width = rng.int(2, 10);
      const height = rng.int(2, 12);
      const volume = length * width * height;
      return numericExercise({
        prompt: `A box has a volume of ${volume} cm³. Its base is ${length} cm by ${width} cm. How tall is it?`,
        unit: 'cm',
        value: height,
        summary: `The base area is ${length * width} cm², so the height is ${volume} ÷ ${length * width} = ${height} cm.`,
        hint: 'Volume is base area × height, so divide the volume by the base area.',
        steps: [
          { label: 'Find the base area.', latex: `${length} \\times ${width} = ${length * width}` },
          { label: 'Divide the volume by it.', latex: `${volume} \\div ${length * width} = ${height}` },
        ],
        traps: [trap(length * width, 'volume-as-area')],
      });
    },
  }),

  /* ---------------------------------------------------------- statistics */
  template({
    id: 'read-dot-plot',
    conceptId: 'data-displays',
    description: 'Read a frequency from a dot plot.',
    difficulties: [1, 2, 3],
    build: ({ rng, difficulty }) => {
      const count = difficulty === 1 ? 10 : difficulty === 2 ? 14 : 18;
      const values = Array.from({ length: count }, () => rng.int(1, 6));
      const target = rng.int(1, 6);
      const frequency = values.filter((v) => v === target).length;
      const above = values.filter((v) => v > target).length;
      const askAbove = difficulty === 3;
      return numericExercise({
        prompt: askAbove
          ? `How many values are greater than ${target}?`
          : `How many values are exactly ${target}?`,
        figure: { kind: 'dot-plot', values, from: 1, to: 6, label: 'observed values' },
        value: askAbove ? above : frequency,
        summary: askAbove
          ? `Counting every dot to the right of ${target} gives ${above}.`
          : `There are ${frequency} dots stacked above ${target}.`,
        hint: askAbove ? 'Count the dots to the right — do not include the column itself.' : 'Count the dots in that one column.',
        steps: [
          {
            label: askAbove ? `Count all dots above ${target}.` : `Count the stack over ${target}.`,
            latex: String(askAbove ? above : frequency),
          },
        ],
        // "Greater than" wrongly read as "greater than or equal to".
        ...(askAbove && frequency > 0 ? { traps: [trap(above + frequency, 'ignored-remainder')] } : {}),
      });
    },
  }),

  template({
    id: 'read-bar-chart',
    conceptId: 'data-displays',
    description: 'Read and compare values on a bar chart.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const labels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
      const bars = labels.map((label) => ({ label, value: rng.int(4, 24) }));
      const askTotal = difficulty === 3;
      const tallest = bars.reduce((best, bar) => (bar.value > best.value ? bar : best), bars[0] as { label: string; value: number });
      const total = bars.reduce((sum, bar) => sum + bar.value, 0);
      return numericExercise({
        prompt: askTotal ? 'What is the total across all five days?' : `How many were borrowed on ${tallest.label}?`,
        figure: { kind: 'bar-chart', bars, unitLabel: 'books borrowed' },
        value: askTotal ? total : tallest.value,
        summary: askTotal
          ? `${bars.map((bar) => bar.value).join(' + ')} = ${total}.`
          : `The bar for ${tallest.label} reaches ${tallest.value}.`,
        hint: askTotal ? 'Add every bar.' : 'Read the height of that one bar.',
        steps: [
          {
            label: askTotal ? 'Add the five values.' : 'Read the bar.',
            latex: askTotal ? `${bars.map((bar) => bar.value).join(' + ')} = ${total}` : String(tallest.value),
          },
        ],
      });
    },
  }),

  template({
    id: 'mean-median',
    conceptId: 'mean-median-mode',
    description: 'Compute the mean or median of a small data set.',
    difficulties: [2, 3, 4],
    build: ({ rng, difficulty }) => {
      const size = difficulty === 2 ? 5 : difficulty === 3 ? 5 : 7;
      const values = Array.from({ length: size }, () => rng.int(1, difficulty === 4 ? 20 : 12));
      const sorted = [...values].sort((a, b) => a - b);
      const total = values.reduce((sum, v) => sum + v, 0);
      const askMean = rng.bool();
      const mean = roundTo(total / size, 2);
      const median = sorted[Math.floor(size / 2)] as number;
      // The middle of the *unsorted* list — the classic median error.
      const unsortedMiddle = values[Math.floor(size / 2)] as number;

      return numericExercise({
        prompt: `Find the ${askMean ? 'mean' : 'median'} of: ${values.join(', ')}`,
        figure: { kind: 'dot-plot', values, label: 'the data set' },
        value: askMean ? mean : median,
        summary: askMean
          ? `${values.join(' + ')} = ${total}, and ${total} ÷ ${size} = ${mean}.`
          : `In order: ${sorted.join(', ')}. The middle value is ${median}.`,
        hint: askMean ? 'Add every value, then divide by how many there are.' : 'Sort the values first, then take the middle one.',
        steps: askMean
          ? [
              { label: 'Add every value.', latex: `${values.join(' + ')} = ${total}` },
              { label: `Divide by how many there are (${size}).`, latex: `${total} \\div ${size} = ${mean}` },
            ]
          : [
              { label: 'Sort the values.', latex: sorted.join(', \; ') },
              { label: 'Take the middle one.', latex: String(median) },
            ],
        traps: askMean
          ? [trap(total, 'mean-wrong-divisor'), trap(roundTo(total / (size - 1), 2), 'mean-wrong-divisor')]
          : [trap(unsortedMiddle, 'median-unsorted')],
      });
    },
  }),

  template({
    id: 'mode-find',
    conceptId: 'mean-median-mode',
    description: 'Find the mode of a data set.',
    difficulties: [1, 2],
    build: ({ rng, difficulty }) => {
      const modeValue = rng.int(1, 8);
      const repeats = difficulty === 1 ? 4 : 3;
      const others = Array.from({ length: difficulty === 1 ? 3 : 5 }, () => {
        let v = rng.int(1, 8);
        if (v === modeValue) v = (v % 8) + 1;
        return v;
      });
      // Guarantee a unique mode: every other value appears at most twice.
      const unique = [...new Set(others)];
      const values = rng.shuffle([...Array.from({ length: repeats }, () => modeValue), ...unique]);
      return numericExercise({
        prompt: `What is the mode of: ${values.join(', ')}`,
        figure: { kind: 'dot-plot', values, from: 1, to: 8, label: 'the data set' },
        value: modeValue,
        summary: `${modeValue} appears ${repeats} times, more often than any other value.`,
        hint: 'The mode is the value that appears most often — look for the tallest stack.',
        steps: [
          { label: 'Count how often each value appears.', latex: `${modeValue} \\text{ appears } ${repeats} \\text{ times}` },
          { label: 'The most frequent value is the mode.', latex: String(modeValue) },
        ],
      });
    },
  }),
];
